// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SmartSpyDetectorTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Smart Spy Detector"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Smart
        Spy Detector” (the “App”). By downloading, installing, accessing, or
        using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>
        2) Description of the App and Important Limitations
      </div>
      <p>
        The App is a mobile tool that uses your device sensors (such as the
        magnetometer and camera) to help you look for potential sources of
        magnetic interference and optical reflections that may indicate
        electronic devices in the environment. It provides a “scanner” interface
        with radar-style animations, magnetic field indicators, camera-based
        reflection checks, and optional haptic or sound alerts. The App is
        intended for general informational and educational use only and is not a
        certified security, forensics, or professional counter-surveillance
        product.
      </p>
      <p>
        Sensor readings can be affected by many factors, including nearby
        electronics, building materials, magnets, lighting conditions, device
        orientation, calibration, and sensor quality. The App cannot guarantee
        that it will detect all hidden devices, nor can it confirm that any
        detected anomaly is actually a camera, bug, or spying device. It should
        never be your only method of protecting privacy or evaluating risk.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Sensors, Readings, and Technical Limits</div>
      <p>
        The App uses device sensors (for example, the magnetometer, camera, and
        flashlight) to display readings and simulated scanning effects. These
        sensors and APIs are provided by your device manufacturer and operating
        system and may not be calibrated, precise, or available on all devices.
        Sensor values are approximate and may be noisy or unstable.
      </p>
      <p>
        You acknowledge that:
      </p>
      <ul>
        <li>
          readings may change based on how you hold the device, nearby metal
          objects, magnets, electronics, or interference;
        </li>
        <li>
          the App uses simple thresholds and heuristics (for example, magnetic
          field strength ranges) that are not guaranteed to identify hidden
          devices accurately;
        </li>
        <li>
          camera reflections can come from many harmless surfaces (such as
          glossy materials, screws, glass, or decorations) and may result in
          false alerts;
        </li>
        <li>
          the App may miss actual devices or incorrectly flag ordinary objects.
        </li>
      </ul>
      <p>
        The App does not access or control any spy devices directly and cannot
        “turn off” or disable such devices. It only interprets sensor data and
        presents it in a user-friendly interface.
      </p>

      <div className={s.title}>5) Results and No Guarantee of Detection</div>
      <p>
        Any messages such as “Possible camera detected”, “No hidden devices
        found”, or similar statuses are indications based on simple rules and
        sensor values, not definitive conclusions. The App does not guarantee
        that:
      </p>
      <ul>
        <li>
          all hidden cameras, microphones, or trackers will be detected in a
          given environment;
        </li>
        <li>
          every alert corresponds to an actual spying device;
        </li>
        <li>
          the environment is safe or free from surveillance if no anomalies are
          reported.
        </li>
      </ul>
      <p>
        You are responsible for interpreting results carefully, considering
        context, and taking additional steps if you have serious concerns about
        your privacy or safety (for example, consulting building owners,
        security professionals, or relevant authorities in accordance with local
        law).
      </p>

      <div className={s.title}>6) Free Use, Scan Limits, and Premium Features</div>
      <p>
        The App may offer a free tier with limited usage—for example, a limited
        number of scans, or restrictions on advanced detection tools—alongside a
        premium tier that unlocks unlimited scans or additional features. After
        a threshold of free scans or certain actions, the App may display a
        paywall and restrict further scanning or features unless you subscribe.
      </p>
      <p>
        The exact limits, available tools, and premium benefits may change over
        time. Limits may be tracked locally on your device and/or in connection
        with your purchases. Deleting and reinstalling the App does not
        guarantee that limits will reset, as some eligibility may be managed at
        the store or subscription level.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features—such as unlimited scans, additional scanning modes, or
        advanced alerts—may be made available through auto-renewable
        subscriptions or other in-app purchases (“Subscriptions”), such as
        weekly plans with a free trial period. Exact pricing, trial length, and
        benefits may vary by region and may change over time as displayed in the
        App and in the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features such as unlimited scans or pro detection tools. An internet
        connection may be required to validate or refresh entitlements after
        long offline periods. If platform billing reverses, refunds, or flags a
        transaction as fraudulent, related entitlements may be limited,
        suspended, or revoked.
      </p>

      <div className={s.title}>9) Camera, Flashlight, and Device Permissions</div>
      <p>
        The App may request access to your device camera, flashlight/torch, and
        vibration/sound capabilities to provide scanning visualizations and
        alerts. You can choose whether to grant or revoke these permissions in
        your device settings. If you deny permissions, some or all features (for
        example, optical scan mode) may be unavailable.
      </p>
      <p>
        Camera images are used in real time to display the scene and highlight
        reflections; the MVP is designed without a remote backend for storing or
        uploading camera frames. Nevertheless, you remain responsible for where
        you point the camera and for respecting the privacy and rights of other
        people in accordance with local law (for example, restrictions on
        recording in certain locations).
      </p>

      <div className={s.title}>10) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful and responsible purposes and
        not to:
      </p>
      <ul>
        <li>
          use the App to harass, intimidate, or unreasonably accuse others of
          spying without cause;
        </li>
        <li>
          use sensor readings as a pretext for unlawful entry, property damage,
          or removal of equipment you do not own;
        </li>
        <li>
          use the App to interfere with legitimate, clearly disclosed security
          cameras (for example, in businesses or public areas, where allowed by
          law);
        </li>
        <li>
          infringe the privacy, safety, or rights of others in violation of
          applicable law;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and premium features;
        </li>
        <li>
          use automated tools or scripts to overload, scrape, or misuse the App
          or its services.
        </li>
      </ul>

      <div className={s.title}>11) Safety and Your Decisions</div>
      <p>
        Results displayed by the App should not be used as the sole basis for
        important safety or legal decisions. If you believe that your privacy or
        safety is at risk, you should rely on multiple sources of information
        and, where appropriate, seek help from building management, security
        professionals, or relevant authorities according to local law.
      </p>
      <p>
        You must not put yourself or others in danger while using the App (for
        example, by entering restricted areas, climbing into hazardous spaces,
        or using the App while driving or operating machinery).
      </p>

      <div className={s.title}>12) Intellectual Property</div>
      <p>
        You retain ownership of any notes, labels, or other data you manually
        enter into the App, subject to rights of third parties. We may process
        such data internally to operate, maintain, and improve the App (for
        example, to display scan history or settings).
      </p>
      <p>
        All software, user interface designs, radar animations, icons, logos,
        and other materials provided with the App (excluding your data and
        third-party components) are owned by the publisher or its licensors and
        are protected by intellectual property laws. Subject to these Terms and
        any Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its features for
        personal privacy-checking and informational purposes.
      </p>

      <div className={s.title}>13) Analytics and Crash Reporting</div>
      <p>
        We may use analytics and crash-reporting tools (for example, Firebase
        Analytics and Crashlytics or similar services) to understand how the App
        is used, monitor performance, and improve stability. Typical events may
        include app opens, scan starts and completions, alerts triggered,
        paywall views, and subscription outcomes. Aggregated or de-identified
        data may be used to guide product decisions. Where required by law, we
        will obtain necessary permissions or provide options to manage analytics
        preferences.
      </p>

      <div className={s.title}>14) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          the App will detect all hidden cameras, microphones, or tracking
          devices, or correctly identify them;
        </li>
        <li>
          sensor readings will be accurate, reliable, or complete in every
          environment;
        </li>
        <li>
          the App will operate without interruption, delay, or defects on all
          devices or OS versions.
        </li>
      </ul>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data, or business interruption arising from or related to your use of or
        inability to use the App, including reliance on any detection or
        non-detection results. Our total aggregate liability to you for direct
        damages arising out of or relating to the App will be limited to the
        amount you paid for Subscriptions during the twelve (12) months prior to
        the claim, or the minimum amount permitted by applicable law if greater.
        Some jurisdictions do not allow certain limitations or exclusions, so
        parts of this section may not apply to you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your interpretation of sensor
        readings, your actions based on App results, your breach of these Terms,
        or your violation of any rights of a third party.
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Smart Spy Detector, which explains how information is collected, used,
        stored, and shared, including device information, settings, and
        analytics. In case of conflict between these Terms and the Privacy
        Policy regarding data processing, the Privacy Policy will guide how we
        handle personal data, while these Terms govern your use of the App and
        its features. You should review the Privacy Policy regularly to
        understand current practices.
      </p>

      <div className={s.title}>19) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing app-specific or
        purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
